import type {
  AgentCatalog,
  AgentSuggestion,
  CaApiClient,
  CaReply,
  CaStreamHandlers,
  ConversationMeta,
  DashboardAgentPresence,
  ShareEntry,
  Turn,
} from './types'

export interface CaApiClientOpts {
  apiBaseUrl?: string
  fetchImpl?: typeof fetch
  authToken?: string | (() => string | undefined)
}

export function createCaApiClient(opts: CaApiClientOpts = {}): CaApiClient {
  const base = (opts.apiBaseUrl ?? '').replace(/\/$/, '')
  if (!base) {
    throw new Error('CA chat: API base URL is not configured')
  }
  const f = opts.fetchImpl ?? fetch

  function currentToken(): string | undefined {
    return typeof opts.authToken === 'function' ? opts.authToken() : opts.authToken
  }

  async function req<T>(path: string, init?: RequestInit): Promise<T> {
    const token = currentToken()
    const res = await f(`${base}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers as Record<string, string> | undefined),
      },
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(`CA API ${res.status}: ${text}`)
    }
    if (res.status === 204) { return undefined as unknown as T }
    const body = (await res.json()) as unknown
    if (
      body &&
      typeof body === 'object' &&
      'data' in (body as Record<string, unknown>) &&
      'status' in (body as Record<string, unknown>) &&
      '_metadata' in (body as Record<string, unknown>)
    ) {
      return (body as { data: T }).data
    }
    return body as T
  }

  return {
    listConversations: (dashboardId) =>
      req<ConversationMeta[]>(
        dashboardId
          ? `/conversations?dashboard_id=${encodeURIComponent(dashboardId)}`
          : '/conversations',
      ),
    listAgents: () => req<AgentCatalog>('/conversations/agents'),
    createConversation: (title, opts) =>
      req<ConversationMeta>('/conversations', {
        method: 'POST',
        body: JSON.stringify({
          title,
          backend: opts?.backend,
          agent_id: opts?.agentId,
          dashboard_id: opts?.dashboardId,
        }),
      }),
    getDashboardAgent: (dashboardId) =>
      req<DashboardAgentPresence | null>(
        `/dashboards/${encodeURIComponent(dashboardId)}/agent`,
      ),
    setDashboardAgent: (dashboardId, agentId, backend) =>
      req<void>(`/dashboards/${encodeURIComponent(dashboardId)}/agent`, {
        method: 'PUT',
        body: JSON.stringify({ agent_id: agentId, backend }),
      }),
    removeDashboardAgent: (dashboardId) =>
      req<void>(`/dashboards/${encodeURIComponent(dashboardId)}/agent`, {
        method: 'DELETE',
      }),
    listAgentsForDashboard: (dashboardId) =>
      req<AgentSuggestion[]>(
        `/conversations/agents/for-dashboard/${encodeURIComponent(dashboardId)}`,
      ),
    getMessages: (id) => req<Turn[]>(`/conversations/${id}/messages`),
    ask: (id, question) =>
      req<CaReply>(`/conversations/${id}/chat`, { method: 'POST', body: JSON.stringify({ question }) }),
    askStream: (id, question, handlers) =>
      streamChat(`${base}/conversations/${id}/chat/stream`, question, f, currentToken(), handlers),
    renameConversation: (id, title) =>
      req<ConversationMeta>(`/conversations/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
    deleteConversation: (id) => req<void>(`/conversations/${id}`, { method: 'DELETE' }),
    shareConversation: (id, targetUserId, role) =>
      req<void>(`/conversations/${id}/share`, {
        method: 'POST',
        body: JSON.stringify({ target_user_id: targetUserId, ...(role ? { role } : {}) }),
      }),
    unshareConversation: (id, targetUserId) =>
      req<void>(`/conversations/${id}/share/${targetUserId}`, { method: 'DELETE' }),
    listShares: (id) => req<ShareEntry[]>(`/conversations/${id}/shares`),
  }
}

async function streamChat(
  url: string,
  question: string,
  f: typeof fetch,
  token: string | undefined,
  handlers?: CaStreamHandlers,
): Promise<CaReply> {
  const res = await f(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ question }),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`CA API ${res.status}: ${text}`)
  }
  const body = (res as unknown as { body?: ReadableStream<Uint8Array> }).body
  if (!body || typeof body.getReader !== 'function') {
    throw new Error('CA_STREAM_UNSUPPORTED')
  }

  const reader = body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullText = ''
  let finalReply: CaReply | undefined
  let completed = false

  const handleEvent = (raw: string) => {
    let evt: any
    try {
      evt = JSON.parse(raw)
    } catch {
      return
    }
    if (evt.error) { throw new Error(String(evt.error)) }
    if (evt.done) {
      completed = true
      finalReply = evt.reply as CaReply
      return
    }
    const sys = evt.message?.systemMessage
    const parts = sys?.text?.parts
    if (Array.isArray(parts)) {
      if (sys.text?.textType === 'THOUGHT') {
        const thought = parts.filter(Boolean).join('\n')
        if (thought) { handlers?.onThought?.(thought) }
      } else {
        const joined = parts.join('')
        if (joined) {
          fullText += joined
          handlers?.onText?.(fullText)
        }
      }
    }
  }

  for (;;) {
    const { value, done } = await reader.read()
    if (done) { break }
    buffer += decoder.decode(value, { stream: true })
    const { events, rest } = splitJsonEvents(buffer)
    buffer = rest
    events.forEach(handleEvent)
  }
  if (buffer.trim()) { handleEvent(buffer.trim()) }
  if (!completed) {
    throw new Error('CA stream ended before completion')
  }

  return finalReply ?? { text: fullText || undefined, tables: [], charts: [] }
}

function splitJsonEvents(buffer: string): { events: string[]; rest: string } {
  const events: string[] = []
  let depth = 0
  let inString = false
  let escaped = false
  let start = -1
  for (let i = 0; i < buffer.length; i++) {
    const ch = buffer[i]
    if (inString) {
      if (escaped) {
        escaped = false
      } else if (ch === '\\') {
        escaped = true
      } else if (ch === '"') {
        inString = false
      }
      continue
    }
    if (ch === '"') {
      inString = true
    } else if (ch === '{') {
      if (depth === 0) { start = i }
      depth += 1
    } else if (ch === '}' && depth > 0) {
      depth -= 1
      if (depth === 0) {
        events.push(buffer.slice(start, i + 1))
        start = -1
      }
    }
  }
  return { events, rest: start >= 0 ? buffer.slice(start) : '' }
}
